import { useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'

import Card from '@10xLabs/react-web/components/Card'
import { StyledLink } from 'components/v2/Link'
import { Loader } from '@10xLabs/react-web/components/Loader'
import Breadcrumb from 'components/Breadcrumb'
import { Button } from 'components/v2/Button'
import { Content, ContentHeader } from 'components/v2/Content'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { TripIncidenceCouponsContent } from 'features/Trips/components/TripIncidenceCouponsContent'
import { GET_TRIP_SUBCAMPAIGN } from 'features/Trips/queries-legacy'
import { GTIncidenceSubcampaign } from 'gql/types.generated'
import { useLogin } from 'features/Login/hooks'
import { getQueries } from 'utils/route'
import { canReadPassengers } from 'features/Users/utils'

const messages = defineMessages({
  helmet: {
    id: 'trip.incidenceCoupons.helmet',
    defaultMessage: 'Trip Incidence Coupons | Nexbus Admin'
  }
})

const TripIncidenceCoupons = (): ReactElement => {
  const { subcampaignId } = getQueries(location.search)
  const intl = useIntl()
  const login = useLogin()
  const hasAccess = canReadPassengers(login.accessToken.policies)

  let content

  const { loading, error, data, refetch, networkStatus } = useQuery<{
    incidenceSubcampaign: GTIncidenceSubcampaign
  }>(GET_TRIP_SUBCAMPAIGN, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        id: subcampaignId
      }
    }
  })

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
    if (hasAccess) {
      refetch()
    }
  }, [])

  if (!hasAccess) {
    return <ForbiddenView feature={FEATURES.PASSENGERS} />
  }
  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <Card.Container>
        <Card.Content noHeader noFooter>
          <EmptyState.Container>
            <Loader />
          </EmptyState.Container>
        </Card.Content>
      </Card.Container>
    )
  } else if (error || isEmpty(data)) {
    content = (
      <Card.Container>
        <Card.Content noHeader noFooter>
          <EmptyState.Container>
            <EmptyState.Description>
              <FormattedMessage
                id="trip.incidenceCoupons.error.description"
                defaultMessage="There was an error while loading your data"
              />
            </EmptyState.Description>

            <EmptyState.Action>
              <Button
                onClick={(): void => {
                  refetch()
                }}
                kind="primary"
              >
                <FormattedMessage id="trip.incidenceCoupons.error.action" defaultMessage="Reload" />
              </Button>
            </EmptyState.Action>
          </EmptyState.Container>
        </Card.Content>
      </Card.Container>
    )
  } else {
    content = <TripIncidenceCouponsContent subcampaign={data.incidenceSubcampaign} />
  }

  return (
    <Content>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="trip.incidenceCoupons.header.label" defaultMessage="Trip Incidence Coupons" />
        </Heading.H3>
      </ContentHeader>
      <Breadcrumb.Container>
        <Breadcrumb.Item>
          <StyledLink as={NavLink} to="/trips">
            <FormattedMessage id="dictionary.trips" defaultMessage="Trips" />
          </StyledLink>
        </Breadcrumb.Item>
      </Breadcrumb.Container>
      <section>{content}</section>
    </Content>
  )
}

export default withLastUrl(TripIncidenceCoupons)
